/**
 * Dragging a sheet down (UX spec v3 §C.8; numbers from §C.16; the design's
 * `shared.js` `bindSheet`, ported). The sheet follows the finger 1:1 and the
 * backdrop fades with it; released past 30 % of its height or faster than
 * 0.5 px/ms it leaves, otherwise it springs back; up is rubber-banded to 24 px.
 * A locked sheet (a ceremony in flight) resists 16 px either way and stays.
 * The body scrolls first: a drag starts only from `scrollTop 0`, only when the
 * movement is more vertical than horizontal, and a scrollable body pulled up
 * scrolls instead.
 */
const SLOP = 10;
const UP_MAX = 24;
const LOCKED_MAX = 16;
const DISMISS_AT = 0.3;
const FLICK = 0.5;

export const rubber = (d: number, max: number): number => max * (1 - Math.exp(-d / (max * 2.2)));

export function dragOffset(dy: number, locked: boolean): number {
  if (locked) return dy > 0 ? rubber(dy, LOCKED_MAX) : -rubber(-dy, LOCKED_MAX);
  return dy > 0 ? dy : -rubber(-dy, UP_MAX);
}

export function release(y: number, height: number, velocity: number, locked: boolean): "dismiss" | "spring" {
  if (locked) return "spring";
  return y > DISMISS_AT * height || velocity > FLICK ? "dismiss" : "spring";
}

/** 140–240 ms, shorter the further the sheet has already travelled (§C.7). */
export function exitMs(y: number, height: number): number {
  const h = height > 0 ? height : 400;
  return Math.round(Math.max(140, Math.min(240, 240 * (1 - Math.max(0, y) / h) + 60)));
}

export interface DragHooks {
  locked(): boolean;
  reduced(): boolean;
  /** Released past the threshold: the sheet leaves from where the finger left it. */
  dismiss(fromY: number): void;
}

/**
 * Bind the drag to one sheet. Pointer capture keeps the finger's moves on the
 * sheet; a non-passive `touchmove` stops the page from taking the gesture as a
 * scroll once the drag has started (iOS would otherwise send `pointercancel`).
 */
export function bindDrag(sheet: HTMLElement, backdrop: HTMLElement, hooks: DragHooks): void {
  sheet.dataset["drag"] = "bound";
  let g: { x0: number; y0: number; id: number; moved: boolean; y: number; h: number; s: [number, number][] } | null = null;

  sheet.addEventListener("pointerdown", (e) => {
    if (e.button > 0) return;
    g = { x0: e.clientX, y0: e.clientY, id: e.pointerId, moved: false, y: 0, h: sheet.getBoundingClientRect().height, s: [[e.clientY, e.timeStamp]] };
  });
  sheet.addEventListener("pointermove", (e) => {
    if (g === null) return;
    const dy = e.clientY - g.y0;
    if (!g.moved) {
      if (Math.abs(dy) < SLOP || Math.abs(dy) < Math.abs(e.clientX - g.x0)) return;
      // The body scrolls first: down only from the top, never up while it can scroll.
      if ((sheet.scrollTop > 0 && dy > 0) || (sheet.scrollHeight > sheet.clientHeight + 1 && dy < 0)) {
        g = null;
        return;
      }
      g.moved = true;
      try {
        sheet.setPointerCapture(g.id);
      } catch {
        /* a synthetic pointer has nothing to capture */
      }
      sheet.classList.add("dragging");
      backdrop.classList.add("dragging");
    }
    const locked = hooks.locked();
    const y = dragOffset(dy, locked);
    g.y = y;
    sheet.style.transform = `translateY(${String(y)}px)`;
    backdrop.style.opacity = String(locked ? 1 : Math.max(0, 1 - Math.max(0, y) / g.h));
    g.s.push([e.clientY, e.timeStamp]);
    if (g.s.length > 6) g.s.shift();
  });
  const end = (): void => {
    const gg = g;
    g = null;
    if (gg === null || !gg.moved) return;
    const a = gg.s[0] as [number, number];
    const b = gg.s[gg.s.length - 1] as [number, number];
    const v = (b[0] - a[0]) / Math.max(1, b[1] - a[1]);
    sheet.classList.remove("dragging");
    backdrop.classList.remove("dragging");
    // A drag is not a tap: the click the release would fire on a button underneath is swallowed.
    const swallow = (ev: Event): void => {
      ev.stopPropagation();
      ev.preventDefault();
    };
    sheet.addEventListener("click", swallow, { capture: true, once: true });
    setTimeout(() => sheet.removeEventListener("click", swallow, { capture: true }), 50);
    sheet.dataset["last"] = JSON.stringify({ y: Math.round(gg.y), v: Math.round(v * 100) / 100 });
    if (release(gg.y, gg.h, v, hooks.locked()) === "dismiss") {
      hooks.dismiss(gg.y);
      return;
    }
    const reduced = hooks.reduced();
    sheet.style.transition = reduced ? "none" : "transform var(--t-rebound) var(--ease-spring)";
    sheet.style.transform = "";
    backdrop.style.transition = reduced ? "none" : "opacity var(--t-rebound) linear";
    backdrop.style.opacity = "";
    setTimeout(() => {
      sheet.style.transition = "";
      backdrop.style.transition = "";
    }, 300);
  };
  sheet.addEventListener("pointerup", end);
  sheet.addEventListener("pointercancel", end);
  sheet.addEventListener(
    "touchmove",
    (e) => {
      if (g?.moved === true) e.preventDefault();
    },
    { passive: false },
  );
}
